import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { fetchMe } from "../services/workHoursService";
import { buildThemeClasses } from "../theme";

// ---------------------------------------------------------------------------
// Secciones solo para los dueños (#1064): Clientes y Reportes. Quién es dueño
// lo decide el SERVIDOR (`/workHoursMe` → `isAdmin`: en CC-CONSOLE, correo
// verificado de Isaías o Daniel, #1007; en otros tenants, el rol admin). Esto
// solo evita pintar una pantalla que la API va a rechazar con 403 — el candado
// real está en el backend.
// ---------------------------------------------------------------------------

type Access = "loading" | "owner" | "denied" | "unknown";

/** `loading` mientras pregunta; si `/workHoursMe` falla es `unknown` (no «denegado»). */
export function useWorkHoursOwner(): Access {
  const [access, setAccess] = useState<Access>("loading");
  useEffect(() => {
    let alive = true;
    fetchMe()
      .then((me) => alive && setAccess(me?.isAdmin ? "owner" : "denied"))
      .catch(() => alive && setAccess("unknown"));
    return () => {
      alive = false;
    };
  }, []);
  return access;
}

export default function OwnersOnly({ children }: { children: ReactNode }) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const access = useWorkHoursOwner();

  if (access === "loading") return <div data-testid="owners-only-loading" className="p-6" />;
  // Si no se pudo saber, la sección se pinta y su propia carga dirá qué pasó
  // (un 403 se explica ahí; un corte de red no es un problema de permisos).
  if (access === "owner" || access === "unknown") return <>{children}</>;
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-10 text-center" data-testid="owners-only">
      <i className={`ri-lock-2-line text-3xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
      <p className={`text-lg font-medium ${tc.text}`}>{t("workHours.ownersOnly.title")}</p>
      <p className={`max-w-md text-sm ${tc.textSecondary}`}>{t("workHours.ownersOnly.text")}</p>
    </div>
  );
}
